import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import InfoIcon from "@mui/icons-material/Info";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import SendIcon from "@mui/icons-material/Send";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useCustomTranslation } from "../../hooks/useCustomTranslation";
import { translateApiError } from "../../utils/apiErrorTranslation";
import DataGrid from "../../widgets/DataGrid/DataGrid";
import CustomTextField from "../../widgets/CustomTextField/CustomTextField";
import CustomDatePicker from "../../widgets/CustomDatePicker/CustomDatePicker";
import CustomSelect from "../../widgets/CustomSelect/CustomSelect";
import AddPromiseAmount from "./AddPromiseAmount";
import EditPromise from "./EditPromise";
import { useApiClientMutation, useApiClientQuery } from "../../hooks/useApiClient";
import api_endpoints from "../../api";
import { PAYMENT_TYPE, STATUS_TYPE } from "../../enums/PromiseEnums";
import {
  currencyFormatter,
  dateTimeReadableSimple,
  formatDateTime,
} from "../../utils";

export const PromiseDetails = () => {
  const { state } = useLocation();
  const initialPromiseId = state?.promise_id || "";
  const { t } = useCustomTranslation("Promise Details");
  const [selectedPromiseId, setSelectedPromiseId] = useState(initialPromiseId);
  const [rows, setRows] = useState([]);
  const [openAmountModal, setOpenAmountModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [sendMessage, setSendMessage] = useState("");
  const [tableFilter, setTableFilter] = useState({
    amount: "",
    donation_date: "",
    inserted_by: "",
    payment_type: "",
  });

  const promisesQuery = useApiClientQuery(
    () => api_endpoints.getPromisesForDonorCampaign(initialPromiseId),
    {
      queryKey: ["promise_donor_campaign", initialPromiseId, refreshKey],
      enabled: Boolean(initialPromiseId),
    }
  );

  const promises = useMemo(
    () => (Array.isArray(promisesQuery.data?.promises) ? promisesQuery.data.promises : []),
    [promisesQuery.data]
  );

  useEffect(() => {
    if (!promises.length) return;
    const exists = promises.some((promise) => String(promise._id) === String(selectedPromiseId));
    if (!exists) {
      setSelectedPromiseId(promises[0]._id);
    }
  }, [promises, selectedPromiseId]);

  const selectedPromise = useMemo(
    () => promises.find((promise) => String(promise._id) === String(selectedPromiseId)) || promises[0],
    [promises, selectedPromiseId]
  );
  const hasActiveSchedule = ["active", "paused"].includes(
    selectedPromise?.subscription_id?.status
  );

  const promiseTransactionsQuery = useApiClientQuery(
    () =>
      api_endpoints.getCampaignPromiseTransactions(
        selectedPromiseId,
        tableFilter.amount,
        tableFilter.donation_date ? formatDateTime(tableFilter.donation_date) : null,
        tableFilter.inserted_by,
        tableFilter.payment_type
      ),
    {
      queryKey: ["view_promise_transactions", selectedPromiseId, tableFilter, refreshKey],
      enabled: Boolean(selectedPromiseId),
    },
    (data) => {
      const formattedData = (data || []).map((transaction) => ({
        id: transaction._id,
        amount: currencyFormatter(transaction.amount),
        donation_date: dateTimeReadableSimple(
          transaction.paymentDetails?.date_of_donation ??
            transaction.paymentDetails?.date_of_transfer
        ),
        payment_type: t(transaction.paymentType),
        inserted_by: `${transaction.paymentDetails?.user?.first_name || ""} ${
          transaction.paymentDetails?.user?.last_name || ""
        }`.trim(),
      }));
      setRows(formattedData);
    }
  );

  const sendLinkMutation = useApiClientMutation(
    () => api_endpoints.sendPromisePaymentLink(selectedPromiseId),
    [],
    () => setSendMessage(t("promise_payment_link_sent", "Payment link sent to the donor."))
  );

  const columns = [
    { field: "amount", headerName: t("amount"), sortable: true, numberSort: true },
    { field: "donation_date", headerName: t("donation_date"), sortable: true },
    { field: "payment_type", headerName: t("payment_type"), sortable: true },
    { field: "inserted_by", headerName: t("inserted_by"), sortable: true },
  ];

  const changeTableFilter = (option, value) => {
    setTableFilter((previous) => ({ ...previous, [option]: value }));
  };

  const filters = {
    amount: (
      <CustomTextField
        value={tableFilter.amount}
        onChange={(event) => changeTableFilter("amount", event.target.value)}
        clearFilter={() => changeTableFilter("amount", "")}
      />
    ),
    donation_date: (
      <CustomDatePicker
        value={tableFilter.donation_date}
        onChange={(value) => changeTableFilter("donation_date", value)}
        clearFilter={() => changeTableFilter("donation_date", "")}
      />
    ),
    payment_type: (
      <CustomSelect
        value={tableFilter.payment_type}
        onChange={(event) => changeTableFilter("payment_type", event.target.value)}
        options={[
          { value: "", label: t("all_text") },
          { value: PAYMENT_TYPE.CASH, label: t("cash") },
          { value: PAYMENT_TYPE.CHEQUE, label: t("cheque") },
          { value: PAYMENT_TYPE.BANK_TRANSFER, label: t("bank_transfer") },
          { value: PAYMENT_TYPE.CREDIT_CARD, label: t("credit_card") },
        ]}
      />
    ),
    inserted_by: (
      <CustomTextField
        value={tableFilter.inserted_by}
        onChange={(event) => changeTableFilter("inserted_by", event.target.value)}
        clearFilter={() => changeTableFilter("inserted_by", "")}
      />
    ),
  };

  const refreshPromiseView = () => {
    setOpenAmountModal(false);
    setOpenEditModal(false);
    setTimeout(() => setRefreshKey((value) => value + 1), 400);
  };

  const statusColor = {
    [STATUS_TYPE.IN_PROGRESS]: "primary",
    [STATUS_TYPE.ABANDONED]: "error",
    [STATUS_TYPE.CLOSED]: "success",
    [STATUS_TYPE.OPEN]: "success",
    [STATUS_TYPE.CANCELLED]: "warning",
  }[selectedPromise?.status] || "default";

  const donorDetails = [
    {
      label: t("email", "Email"),
      value: selectedPromise?.donor_profile?.email || t("not_set", "Not set"),
    },
    {
      label: t("name", "Name"),
      value:
        `${selectedPromise?.donor_profile?.first_name || ""} ${selectedPromise?.donor_profile?.last_name || ""}`.trim() ||
        t("not_set", "Not set"),
    },
    {
      label: t("phone", "Phone"),
      value: selectedPromise?.donor_profile?.phone || t("not_set", "Not set"),
    },
    {
      label: t("address", "Address"),
      value: selectedPromise?.donor_profile?.adresse || t("not_set", "Not set"),
    },
    {
      label: t("company", "Company"),
      value: selectedPromise?.donor_profile?.company || t("not_set", "Not set"),
    },
  ];

  const amountSummary = [
    {
      label: t("promised_amount", "Promised Amount"),
      value: currencyFormatter(selectedPromise?.promise_amount || 0),
    },
    {
      label: t("given_amount", "Given Amount"),
      value: currencyFormatter(selectedPromise?.given_amount || 0),
    },
    {
      label: t("amount_left", "Amount left"),
      value: currencyFormatter(selectedPromise?.remaining_amount || 0),
    },
  ];

  const detailRows = [
    {
      icon: <CalendarMonthIcon />,
      label: t("due_date"),
      value: selectedPromise?.due_date ? formatDateTime(selectedPromise.due_date) : t("none", "None"),
    },
    {
      icon: <InfoIcon />,
      label: t("status"),
      value: <Chip label={t(selectedPromise?.status)} color={statusColor} size="small" />,
    },
    {
      icon: <ReceiptLongIcon />,
      label: t("type_of_promise"),
      value: t(selectedPromise?.promise_type),
    },
    {
      icon: <CalendarMonthIcon />,
      label: t("end_date"),
      value: selectedPromise?.end_date ? formatDateTime(selectedPromise.end_date) : t("not_set", "Not set"),
    },
    {
      icon: <AutorenewOutlinedIcon fontSize="small" />,
      label: t("frequency"),
      value: selectedPromise?.payment_frequency
        ? t(selectedPromise.payment_frequency)
        : t("not_set", "Not set"),
    },
    {
      icon: <CreditCardOutlinedIcon fontSize="small" />,
      label: t("type_of_payment"),
      value: t(selectedPromise?.payment_type),
    },
  ];

  if (!initialPromiseId) {
    return <Alert severity="error">{t("promise_not_found", "Promise not found.")}</Alert>;
  }

  return (
    <div className="flex flex-col gap-y-4">
      <Dialog open={openEditModal} onClose={() => setOpenEditModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("edit_promise")}</DialogTitle>
        <DialogContent>
          <EditPromise
            onClose={refreshPromiseView}
            promise_id={selectedPromiseId}
            promise_amount={selectedPromise?.promise_amount}
            status={selectedPromise?.status}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={openAmountModal} onClose={() => setOpenAmountModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("add_promise_button")}</DialogTitle>
        <DialogContent>
          <AddPromiseAmount
            onClose={refreshPromiseView}
            promise_id={selectedPromiseId}
            payment_type={selectedPromise?.payment_type}
          />
        </DialogContent>
      </Dialog>

      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          {`${t("fundraiser_for", "Fundraiser for")} ${selectedPromise?.campaign?.name || ""}`}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<SendIcon />}
          onClick={() => sendLinkMutation.mutate()}
          disabled={
            !selectedPromiseId ||
            sendLinkMutation.isPending ||
            [STATUS_TYPE.CLOSED, STATUS_TYPE.ABANDONED, STATUS_TYPE.CANCELLED].includes(selectedPromise?.status) ||
            Number(selectedPromise?.remaining_amount || 0) <= 0 ||
            hasActiveSchedule
          }
        >
          {sendLinkMutation.isPending
            ? t("sending", "Sending...")
            : t("send_payment_link", "Send Payment Link")}
        </Button>
      </Stack>

      {sendMessage ? <Alert severity="success" onClose={() => setSendMessage("")}>{sendMessage}</Alert> : null}
      {sendLinkMutation.isError ? (
        <Alert severity="error">
          {translateApiError(
            t,
            sendLinkMutation.error?.response?.data?.message || "promise_payment_link_failed",
            "Failed to send payment link."
          )}
        </Alert>
      ) : null}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            lg: "minmax(340px, 0.9fr) minmax(0, 2.55fr)",
          },
          alignItems: "stretch",
          gap: 2,
        }}
      >
        <Box
          sx={{
            position: { xs: "relative", lg: "relative" },
            minWidth: 0,
            minHeight: { xs: "auto", lg: 0 },
          }}
        >
          <Paper
            variant="outlined"
            sx={{
              p: { xs: 2, md: 2.5 },
              borderRadius: 3,
              minWidth: 0,
              position: { xs: "relative", lg: "absolute" },
              inset: { lg: 0 },
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {t("donor_details", "Donor Details")}
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minHeight: 0,
              }}
            >
              {donorDetails.map((item, index) => (
                <Box
                  key={item.label}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "minmax(82px, 0.7fr) minmax(0, 1.3fr)",
                    alignItems: "center",
                    columnGap: 1.5,
                    flex: 1,
                    minHeight: 48,
                    py: 1,
                    borderBottom: index < donorDetails.length - 1 ? 1 : 0,
                    borderColor: "divider",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      fontWeight: 600,
                      lineHeight: 1.35,
                    }}
                  >
                    {item.label}
                  </Typography>
                  <Typography
                    sx={{
                      color: "text.primary",
                      fontWeight: 700,
                      lineHeight: 1.4,
                      textAlign: "right",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {item.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "minmax(320px, 1fr) minmax(480px, 1.45fr)",
            },
            alignItems: "stretch",
            gap: 2,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              position: { xs: "relative", md: "relative" },
              minWidth: 0,
              minHeight: { xs: 320, md: 0 },
            }}
          >
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 3,
                position: { xs: "relative", md: "absolute" },
                inset: { md: 0 },
                height: { xs: 320, md: "auto" },
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
                overflow: "hidden",
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, flexShrink: 0 }}>
                {t("promises", "Promises")}
              </Typography>
              <Stack
                spacing={1.1}
                sx={{
                  minHeight: 0,
                  flex: 1,
                  overflowY: "auto",
                  overflowX: "hidden",
                  pr: 0.5,
                }}
              >
                {promises.map((promise, index) => {
                  const active = String(promise._id) === String(selectedPromiseId);
                  return (
                    <Paper
                      key={promise._id}
                      variant="outlined"
                      onClick={() => {
                        setSelectedPromiseId(promise._id);
                        setSendMessage("");
                      }}
                      sx={{
                        p: 1.4,
                        cursor: "pointer",
                        flexShrink: 0,
                        borderWidth: active ? 2 : 1,
                        borderColor: active ? "primary.main" : "divider",
                        backgroundColor: active ? "action.selected" : "background.paper",
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" spacing={1}>
                        <Typography sx={{ fontWeight: 700 }}>
                          {t("promise_number", { defaultValue: `Promise ${index + 1}`, number: index + 1 })}
                        </Typography>
                        <Chip
                          size="small"
                          label={t(promise.status)}
                          color={promise.status === STATUS_TYPE.CLOSED ? "success" : "primary"}
                        />
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {currencyFormatter(promise.promise_amount)} • {dateTimeReadableSimple(promise.created_at)}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                        {t("amount_left", "Amount left")}: {currencyFormatter(promise.remaining_amount)}
                      </Typography>
                    </Paper>
                  );
                })}
              </Stack>
            </Paper>
          </Box>

          <Paper
            variant="outlined"
            sx={{
              p: { xs: 2, md: 2.5 },
              borderRadius: 3,
              minWidth: 0,
            }}
          >
            <Stack spacing={2}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {t("promise_details", "Promise Details")}
                </Typography>
                <Button startIcon={<EditIcon />} variant="contained" onClick={() => setOpenEditModal(true)}>
                  {t("edit_promise")}
                </Button>
              </Stack>

              <Divider />

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" },
                  gap: 1.5,
                }}
              >
                {amountSummary.map((item) => (
                  <Box
                    key={item.label}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      backgroundColor: "action.hover",
                      minWidth: 0,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {item.label}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 800, mt: 0.25 }}>
                      {item.value}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                  gap: 1.25,
                }}
              >
                {detailRows.map((item) => (
                  <Box
                    key={item.label}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 1.5,
                      px: 1.25,
                      py: 1.1,
                      borderBottom: 1,
                      borderColor: "divider",
                      minWidth: 0,
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                      <Box sx={{ color: "text.secondary", display: "flex", alignItems: "center" }}>{item.icon}</Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          fontWeight: 600,
                          lineHeight: 1.35,
                        }}
                      >
                        {item.label}
                      </Typography>
                    </Stack>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        color: "text.primary",
                        textAlign: "right",
                        overflowWrap: "anywhere",
                        ml: 1,
                      }}
                    >
                      {item.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Stack>
          </Paper>
        </Box>
      </Box>
      <Divider />
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {t("transactions", "Transactions")}
        </Typography>
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          onClick={() => setOpenAmountModal(true)}
          disabled={!selectedPromiseId || Number(selectedPromise?.remaining_amount || 0) <= 0}
        >
          {t("add_an_amount")}
        </Button>
      </Stack>

      <div className="container-white">
        <DataGrid
          columns={columns}
          rows={rows}
          filters={filters}
          loading={promiseTransactionsQuery.isFetching || promisesQuery.isFetching}
        />
      </div>
    </div>
  );
};
